import columnTypes from './columnTypes'
import filters from './filters'
import accountTypes from './accountTypes'
import type { DataTableDefinition } from '@/types/table'
import FormatValue from '@/helpers/FormatValue'

// Single source of truth for account type labels lives in constants/accountTypes.ts
const accountTypeLabels: Record<string, string> = accountTypes

const multipleUnitDefinition: DataTableDefinition = {
	columns: [
		{
			key: 'hp_id',
			size: 'xs',
			format: FormatValue.getHpIdColumn,
		},
		{
			key: 'building.address.street',
		},
		{
			key: 'position',
		},
		{
			key: 'area',
			format: FormatValue.getFixedFormat,
			hide: 'hidden-md-and-down',
		},
		{
			align: 'center',
			key: 'is_unit',
			format: FormatValue.getCheckOrCrossFromBool,
			hide: 'hidden-md-and-down',
		},
		{
			align: 'center',
			key: 'commercial',
			format: FormatValue.getCheckOrCrossFromBool,
			hide: 'hidden-md-and-down',
		},
	],
	child: {
		name: 'buildingUnits',
		endpoint: 'units',
		params: [
			{
				key: 'id',
				value: 'unitId',
			},
			{
				key: 'building_id',
				value: 'id',
			},
		],
	},
	sortBy: [
		{
			key: 'hp_id',
			order: 'asc',
		},
	],

	icon: 'fal fa-building',
	linkItem: 'units',
	name: 'Einheit(en)',
	linkFirstItem: true,
}

const computeAccountClosingBalance = (item: any): number => {
	const balance = item?.balances?.[0]
	if (!balance) return 0
	const parseDe = (v: unknown) => {
		if (typeof v === 'number') return v
		if (typeof v !== 'string' || v.length === 0) return 0
		const n = parseFloat(v.replace(/\./g, '').replace(',', '.'))
		return Number.isFinite(n) ? n : 0
	}
	const raw =
		parseDe(balance.opening_balance) +
		parseDe(balance.debit) -
		parseDe(balance.credit)
	const t = item?.account_type
	return t === 'S' || t === 'K' || t === 'A' ? -raw : raw
}

const accounts: DataTableDefinition<Api.Account> = {
	columns: [
		{
			key: 'hp_id',
			format: FormatValue.getAccountHpId,
			size: 'sm',
		},
		{
			key: 'name',
			pickKey: 'account_name.name',
			size: 'lg',
		},
		{
			key: 'account_type',
			value: (item: Api.Account) =>
				accountTypeLabels[item.account_type] ?? item.account_type,
			size: 'sm',
		},
		{
			key: 'balance',
			value: (item: Api.Account) => computeAccountClosingBalance(item),
			format: FormatValue.getCurrency,
			align: 'end',
			size: 'sm',
		},
	],
	sortBy: [
		{
			key: 'hp_id',
			order: 'asc',
		},
	],
}

const assfinetContracts: DataTableDefinition = {
	columns: [],
	sortBy: [
		{
			key: 'id',
			order: 'asc',
		},
	],
}
const comments: DataTableDefinition = {
	columns: [],
	sortBy: [
		{
			key: 'id',
			order: 'desc',
		},
	],
}
const journals: DataTableDefinition<Api.Journal> = {
	child: {
		name: 'document',
		endpoint: 'documents',
		params: [
			{
				key: 'receipt.id',
				value: 'id',
			},
		],
	},
	columns: [
		{
			key: 'primanota',
			size: 'xs',
			hide: 'hidden-md-and-down',
		},
		{
			key: 'booking_date',
			format: FormatValue.getLocalDateFromCarbon,
			size: 'sm',
			// Kurzer Spaltenkopf, gleiche Begruendung wie bei Soll/Haben: die
			// Spalte wurde allein von der Beschriftung aufgeblaeht. Die Karten-
			// ansicht behaelt ueber `booking_date` weiterhin 'Buchungsdatum'.
			i18nKey: 'booking_date_short',
			// Datum 71 px + 32 px Zellenpadding; der kurze Kopf passt daneben.
			fixedWidth: 105,
		},
		{
			key: 'contra_account_id',
			// Nummer + Bezeichnung des Gegenkontos. Als pickKey-Array (nicht
			// formatWithItem), damit auch der CSV-Export den Namen bekommt.
			pickKey: [
				'contra_account.account_name.hp_id',
				'contra_account.account_name.name',
			],
			format: FormatValue.getAccountHpIdWithName,
			align: 'start',
			size: 'md',
		},
		{
			key: 'debit',
			format: FormatValue.getCurrency,
			align: 'end',
			size: 'md',
			// Gemessen: '-10.000.000,00 €' ist 106 px breit (14px Roboto), dazu
			// 32 px Zellenpadding. Mehr kann ein Betrag nie brauchen — der
			// gewonnene Platz gehoert dem Buchungstext daneben.
			fixedWidth: 140,
		},
		{
			key: 'credit',
			format: FormatValue.getCurrency,
			align: 'end',
			size: 'md',
			fixedWidth: 140,
		},
		{
			key: 'booking_text',
			size: 'lg',
		},
		{
			align: 'center',
			key: 'document_container_id',
			format: FormatValue.getDocumentContainerDialogIconFromValue,
			size: 'xs',
			sortable: false,
		},

		/*{

            align: 'center',
            key: 'reviews_count',
            size: 'xs',
        },*/
	],
	sortBy: [
		{
			key: 'primanota',
			order: 'desc',
		},
	],
	relations: '["receipt", "comments", "contra_account"]',
	// expand: false,
}
const journalsReviewDocument: DataTableDefinition<Api.JournalReview> = {
	columns: [
		{
			key: 'primanota',
			size: 'xs',
			hide: 'hidden-md-and-down',
		},
		{
			key: 'booking_date',
			format: FormatValue.getLocalDateFromCarbon,
			size: 'sm',
			breakWords: 'keep-all',
		},
		{
			align: 'start',
			key: 'contra_account_id',
			// Nummer + Bezeichnung des Gegenkontos (AAR-1048). Als pickKey-Array
			// (nicht formatWithItem), damit auch der CSV-Export den Namen bekommt.
			pickKey: [
				'contra_account.account_name.hp_id',
				'contra_account.account_name.name',
			],
			format: FormatValue.getAccountHpIdWithName,
			size: 'md',
			hide: 'hidden-lg-and-down',
		},
		{
			key: 'debit',
			format: FormatValue.getCurrency,
			align: 'end',
			size: 'sm',
			breakWords: 'keep-all',
		},
		{
			key: 'credit',
			format: FormatValue.getCurrency,
			align: 'end',
			size: 'sm',
		},
		{
			key: 'booking_text',
			size: 'xl',
		},
		{
			align: 'center',
			key: 'comments_count',
			size: 'xs',
			ifNull: '-',
		},
		{
			align: 'center',
			key: 'document_container_id',
			format: FormatValue.getDocumentContainerDialogIconFromValue,
			size: 'xs',
		},
		{
			align: 'center',
			key: 'review_state',
			format: FormatValue.getReviewStateIcon,
			sortable: false,
		},
	],
	params: {
		count: 'comments',
	},
	sortBy: [
		{
			key: 'booking_date',
			order: 'asc',
		},
	],
	expand: true,
	relations: '["receipt", "comments", "contra_account"]',
	/*usePlaceHolderData: true,*/
}
const sub_invoices: DataTableDefinition<Api.Invoice> = {
	columns: [],
	sortBy: [
		{
			key: 'invoice_date',
			order: 'desc',
		},
	],
}
const invoices: DataTableDefinition<Api.Invoice> = {
	columns: [
		{ key: 'invoice_number', align: 'start' },
		{
			key: 'invoice_date',
			format: FormatValue.getLocalDateFromCarbon,
			hide: 'hidden-xs',
		},
		{ key: 'invoice_amount', format: FormatValue.getCurrency, align: 'end' },
		{
			key: 'authorized_date',
			format: FormatValue.getLocalDateFromCarbon,
			hide: 'hidden-md-and-down',
		},
		{
			key: 'authorized_amount',
			format: FormatValue.getCurrency,
			align: 'end',
			hide: 'hidden-md-and-down',
		},
		{
			key: 'due_date',
			format: FormatValue.getLocalDateFromCarbon,
			hide: 'hidden-md-and-down',
		},
		{
			key: 'booking_text',
			align: 'start',
		},
		{
			key: 'accounting_year.year',
			hide: 'hidden-md-and-down',
		},
		{
			key: 'building_id',
			hide: 'hidden-md-and-down',
			format: FormatValue.getHpId,
		},
		{
			key: 'comments_count',
			align: 'center',
			hide: 'hidden-md-and-down',
		},
	],
	params: {
		count: 'comments',
	},
	sortBy: [
		{
			key: 'invoice_date',
			order: 'desc',
		},
	],
	relations: '["sub_invoices.booking_account"]',
	multiple: sub_invoices,
	icon: 'fal fa-building',
	linkItem: 'sub_invoice',
	name: 'Unterrechung/Position',
	linkFirstItem: true,
}

const faqCategories: DataTableDefinition<Api.FaqCategory> = {
	endpoint: 'faqcategories',
	columns: [],
	sortBy: [{ key: 'ordering', order: 'asc' }],
	relations: '["faqs","faqs.documents"]',
}

const buildings: DataTableDefinition<Api.Building> = {
	relations: '["address", "images", "propertymanager.person"]',
	columns: [
		{
			key: 'hp_id',
			hide: 'hidden-xs',
			format: FormatValue.getHpIdBuildingColumn,
		},
		{
			key: 'address.street',
		},
		{
			key: 'address.zip',
			hide: 'hidden-md-and-down',
		},
		{
			key: 'address.city',
		},
		{
			key: 'building_type.name',
			hide: 'hidden-sm-and-down',
			sortable: false,
		},
		{
			pickKey: [
				'propertymanager.person.firstname',
				'propertymanager.person.lastname',
			],
			key: 'propertymanager.person.lastname',
			hide: 'hidden-sm-and-down',
		},
		{
			// ENUM sortiert nach Definitionsreihenfolge: proposed, not_proposed, missing_pm
			key: 'proposed_for_export',
			align: 'center',
			format: FormatValue.getBuildingExportStatus,
		},
	],
	child: {
		name: 'building',
		endpoint: 'buildings',
		params: [
			{
				key: 'id',
				value: 'id',
			},
		],
	},
	filters: [filters.propertymanagers, filters.teams],
	endpoint: 'buildings',
	sortBy: [
		{
			key: 'hp_id',
			order: 'asc',
		},
	],
}
const buildingsTeam: DataTableDefinition = {
	...buildings,
	...{
		columns: [
			{
				key: 'hp_id',
				hide: 'hidden-xs',
				format: FormatValue.getHpIdColumn,
			},
			{
				key: 'address.street',
			},
			{
				key: 'address.zip',
				hide: 'hidden-md-and-down',
			},
			{
				key: 'address.city',
			},
			{
				key: 'team',
				pickKey: ['team.user.name', 'team.user.email'],
				keyJoin: ' / ',
				sort: 'team_id',
			},
			{
				key: 'proposed_for_export',
				hide: 'hidden-md-and-down',
				align: 'center',
				sortable: false,
				format: FormatValue.getBuildingExportStatus,
			},
		],
		endpoint: 'buildings',
		relations: '["address", "team"]',
	},
}
const contacts: DataTableDefinition<Api.Contact> = {
	columns: [
		{
			key: 'lastname',
			value(item) {
				const items = [
					item.salutation,
					item.title,
					item.firstname,
					item.lastname,
				]
				return items.filter(Boolean).join(' ')
			},
		},
		{
			key: 'company_function',
			hide: 'hidden-md-and-down',
		},
		{
			key: 'phone_number',
			hide: 'hidden-xs',
			// format: FormatValue.getPhoneHref,
		},
		{
			key: 'mail',
			hide: 'hidden-xs',
		},
		/*{ title: 'Straße', key: 'street', hide: 'hidden-md-and-down' },
        { title: 'PLZ', key: 'zip', hide: 'hidden-md-and-down' },
        { title: 'Ort', key: 'city', hide: 'hidden-md-and-down' },*/
	],
	endpoint: 'contact',
	sortBy: [
		{
			key: 'vip',
			order: 'asc',
		},
	],
}
const cis: DataTableDefinition = {
	columns: [
		{
			key: 'id',
			size: 'xs',
		},
		{
			key: 'cis_type.description',
			hide: 'hidden-xs',
		},
		{
			key: 'date',
			format: FormatValue.getLocalDateFromCarbon,
		},
		{
			key: 'time',
		},
		{
			key: 'text',
			sortable: false,
		},
	],
	filters: [filters.cistype],
	endpoint: 'cis',
	sortBy: [
		{
			key: 'date',
			order: 'desc',
		},
	],
}
const cisManagement: DataTableDefinition = {
	columns: [
		{
			key: 'id',
			size: 'xs',
		},
		{
			key: 'date',
			format: FormatValue.getLocalDateFromCarbon,
		},
		{
			key: 'record',
		},
		{
			key: 'text',
			sortable: false,
		},
	],
	child: {
		name: 'cisManagementDialog',
		endpoint: 'cis',
		params: [{ key: 'id', value: 'id' }],
	},
	endpoint: 'cis',
	relations: '["records"]',
	sortBy: [
		{
			key: 'hp_id',
			order: 'desc',
		},
	],
}
const craftsmanTasks: DataTableDefinition = {
	columns: [
		{
			// Portal-angelegte Auftraege haben (noch) keine ERP-hp_id —
			// dann die interne ID zeigen statt einer leeren Spalte, markiert
			// als "im Portal angelegt" (Info ohne eigene Spalte).
			key: 'hp_id',
			ifNull: 'id',
			size: 'sm',
			formatWithItem: FormatValue.getCraftsmanTaskNumber,
		},
		{
			key: 'building',
			pickKey: ['first_building.address.street', 'first_building.address.city'],
			ifNull: [
				'first_unit.building.address.street',
				'first_unit.building.address.city',
			],
			size: 'lg',
			sortable: false,
		},
		{
			key: 'first_unit.position',
			ifNull: '-',
			sortable: false,
			hide: 'hidden-xs',
			size: 'md',
		},
		/*{

            format: FormatValue.getCheckOrCrossFromBool,
            key: 'has_ticket',
            hide: 'hidden-sm-and-down',
            sortable: false,
        },*/
		{
			key: 'to_occur',
			format: FormatValue.getLocalDateFromCarbon,
		},
		{
			key: 'subject_type',
			hide: 'hidden-sm-and-down',
		},
		{
			key: 'status.name',
			hide: 'hidden-xs',
		},
		{
			// AAR-1812: Handwerker (Person, craftsman_person_id) — Relation muss
			// dafuer mitgeladen werden, siehe relations unten.
			pickKey: [
				'craftsman_person.salutation',
				'craftsman_person.firstname',
				'craftsman_person.lastname',
			],
			key: 'craftsman_person.lastname',
			i18nKey: 'craftsman_person',
			// AAR-1812-Regression: bei 'hidden-sm-and-down' war die Spalte schon ab
			// md sichtbar und hat zusammen mit "Betreff" bei 1200px zum horizontalen
			// Scrollen gefuehrt (9 Spalten inkl. Expand-Spalte statt <=8 -> feste statt
			// prozentuale Breiten in TableRow.vue computeColumnStyles). Handwerker
			// erst ab lg zeigen, Betreff bleibt wie gefordert ab md sichtbar.
			hide: 'hidden-md-and-down',
		},
		{
			key: 'subject',
			hide: 'hidden-sm-and-down',
		},
	],
	endpoint: 'craftsman_tasks',
	relations: '["status", "units", "craftsman_person"]',
	child: {
		name: 'craftsmanTask',
		endpoint: 'craftsman_tasks',
		params: [
			{
				key: 'id',
				value: 'craftsmanTaskId',
			},
		],
	},
	filters: [
		filters.craftmanTaskProcess,
		filters.craftmanTaskStatus,
		filters.craftmanTaskType,
		filters.craftsmanTaskTeam,
		filters.craftsmanTaskPerson,
		filters.craftsmanTaskBuildings,
	],
	sortBy: [
		{
			key: 'to_occur',
			order: 'asc',
		},
	],
	multiple: multipleUnitDefinition,
}
const profileReportCraftsmanTasks: DataTableDefinition = {
	...craftsmanTasks,
	child: {
		...craftsmanTasks.child,
		name: 'profileReportCraftsmanTask',
	},
}
const craftsmanTasksBuilding: DataTableDefinition = {
	...craftsmanTasks,
	...{
		hideColumns: ['Liegenschaft', 'Einheit'],
	},
}
const craftsmanTasksReports: DataTableDefinition = {
	...craftsmanTasks,
	...{
		columns: [
			...craftsmanTasks.columns.slice(0, 3),
			{ title: 'Erledigt', key: 'finished' },
			...craftsmanTasks.columns.slice(4),
		],
	},
}
const documents: DataTableDefinition<Api.Document> = {
	child: {
		name: 'document',
		endpoint: 'documents',
		params: [
			{
				key: 'id',
				value: 'id',
			},
		],
	},
	columns: [
		{
			key: 'ts',
			hide: 'hidden-xs',
			format: FormatValue.getUTCDateFromTimestamp,
		},
		{
			key: 'document_type.name',
			hide: 'hidden-xs',
			formatWithItem: (_value, item) =>
				FormatValue.getDocumentTypeLabel(item?.document_type),
		},
		{
			key: 'file_notes',
			align: 'start',
		},
		{
			key: 'posting_year',
			empty: '-',
			hide: 'hidden-md-and-down',
		},
	],
	endpoint: 'documents',
	filters: [filters.documenttype, filters.date],
	relations: '["document_container", "document_type"]',
	sortBy: [
		{
			key: 'ts',
			order: 'desc',
		},
	],
}
const documentsBuildings: DataTableDefinition = {
	...documents,
	...{
		columns: [
			{
				key: 'ts',
				format: FormatValue.getUTCDateFromTimestamp,
				hide: 'hidden-xs',
			},
			{
				key: 'document_type.name',
				hide: 'hidden-xs',
				formatWithItem: (_value, item) =>
					FormatValue.getDocumentTypeLabel(item?.document_type),
			},
			{
				key: 'file_notes',
				align: 'left',
			},
			{
				key: 'document_container.units.position',
				sortable: false,
			},
			{
				key: 'posting_year',
				empty: '-',
				hide: 'hidden-md-and-down',
			},
		],
	},
}
const documentcontainer: DataTableDefinition = {
	child: {
		name: 'document',
		endpoint: 'documents',
		params: [
			{
				key: 'id',
				value: 'id',
			},
		],
	},
	columns: [
		{
			key: 'hp_id',
			hide: 'hidden-xs',
			size: 'xs',
		},
		{
			key: 'comment',
			align: 'start',
		},
		{
			key: 'document_files_count',
			size: 'xs',
		},
	],
	params: {
		count: 'document_files',
	},
	endpoint: 'documentcontainer',
	sortBy: [
		{
			key: 'hp_id',
			order: 'desc',
		},
	],
}
const documenttypes: DataTableDefinition<Api.DocumentType> = {
	columns: [
		{
			key: 'acronym',
			align: 'center',
			hide: 'hidden-md-and-down',
			size: 'md',
		},
		{ key: 'name', ifNull: 'acronym' },
		{ key: 'documents_count', align: 'center' },
		{
			key: 'documents_size',
			align: 'center',
			format: FormatValue.getPrettySize,
		},
	],
	child: {
		name: 'documents',
		endpoint: 'documents',
		filter: filters.documenttype,
		setFilter: true,
	},
	hideColumns: ['Abkürzung', 'Speicherplatz'],
	endpoint: 'documenttypes?os=1',
	relations: '["documents"]',
	params: {
		count: 'documents',
	},
	sortBy: [
		{
			key: 'ordering',
			order: 'asc',
		},
	],
}
const documenttypesBuilding: DataTableDefinition = {
	...documenttypes,
	child: {
		...documenttypes.child,
		name: 'buildingDocumentList',
	},
}
const documenttypesSettings: DataTableDefinition = {
	...documenttypes,
	...{
		columns: [
			{
				key: 'acronym',
				align: 'center',
			},
			{
				key: 'name',
			},
			{
				key: 'display_name',
				ifNull: '-',
			},
			{
				key: 'documents_count',
				hide: 'hidden-md-and-down',
				align: 'end',
			},
			{
				key: 'ordering',
				hide: 'hidden-xs',
				align: 'end',
			},
			{
				key: 'notify',
				align: 'center',
				sortable: true,
				format: FormatValue.getCheckOrCrossFromBool,
			},
			{
				key: 'notify_of_weeks',
				align: 'center',
				sortable: true,
				ifNull: '-',
			},
			{
				key: 'proposed_for_export',
				align: 'center',
				sortable: true,
				format: FormatValue.getCheckOrCrossFromBool,
			},
			{
				key: 'max_age_years',
				align: 'center',
				sortable: true,
				ifNull: '-',
				format: FormatValue.getMaxAgeYearsWithDate,
			},
			{
				key: 'document_cutoff_date',
				align: 'end',
				sortable: true,
				ifNull: '-',
				format: FormatValue.getLocalDateFromCarbon,
			},
		],
		params: {
			count: 'documents',
		},
		child: {
			name: 'documenttypeedit',
			params: [
				{
					key: 'id',
					value: 'id',
				},
			],
		},
		endpoint: 'documenttypes',
	},
}
const emails: DataTableDefinition = {
	columns: [
		{
			key: 'email',
			size: 'lg',
			format: FormatValue.getMailHref,
		},
		{
			key: 'description',
			hide: 'hidden-xs',
			size: 'xs',
		},
		{
			key: 'notice',
			hide: 'hidden-sm-and-down',
			size: 'xs',
		},
	],
	sortBy: [
		{
			key: 'hp_id',
			order: 'asc',
		},
	],
	relatesTo: [
		{
			collection: 'persons',
			column: 'person_id',
		},
	],
}
const notestype: DataTableDefinition = {
	columns: [
		{
			key: 'name',
		},
		{
			key: 'color_header',
		},
	],
	child: {
		name: 'notes',
		endpoint: 'notes',
		filter: filters.notestype,
	},
	endpoint: 'notestype',
	sortBy: [
		{
			key: 'name',
			order: 'asc',
		},
	],
}
const reportTypesSettings: DataTableDefinition = {
	columns: [
		{
			key: 'name',
		},
		{
			key: 'description',
		},
	],
	endpoint: 'report_types',
	sortBy: [
		{
			key: 'name',
			order: 'asc',
		},
	],
}
const notestypeSettings: DataTableDefinition = {
	...notestype,
	...{
		columns: [
			{
				key: 'name',
			},
			{
				key: 'color_header',
				format: FormatValue.getColorBadge,
			},
		],
		child: {
			name: 'notetypeedit',
			params: [
				{
					key: 'id',
					value: 'id',
				},
			],
		},
	},
}
const notestypeFavorites: DataTableDefinition = {
	...notestype,
	...{
		columns: [
			{
				key: 'name',
			},
			{
				key: 'color_header',
				format: FormatValue.getColorBadge,
			},
		],
		child: {
			name: 'notetypeedit',
			params: [
				{
					key: 'id',
					value: 'id',
				},
			],
		},
	},
}
const messages: DataTableDefinition = {
	columns: [
		{
			key: 'building',
			pickKey: [
				'building.address.street',
				'building.address.zip',
				'building.address.city',
			],
			sortable: false,
			keyJoin: ', ',
		},
		{
			hide: 'hidden-xs',
			key: 'unit.position',
			sortable: false,
			default: '-',
		},
		{
			key: 'message',
			sortable: false,
			type: columnTypes.readMore,
		},
		{
			key: 'done',
			align: 'center',
			format: FormatValue.getCheckOrCrossFromBool,
		},
		{
			hide: 'hidden-xs',
			size: 'md',
			key: 'synced_at',
			align: 'center',
			format: FormatValue.getLocalDateTimeFromCarbon,
		},
		{
			key: 'created_at',
			pickKey: 'created',
			hide: 'hidden-xs',
			size: 'md',
			format: FormatValue.getLocalDateTimeFromCarbon,
		},
	],
	endpoint: 'messages',
	sortBy: [
		{
			key: 'created_at',
			order: 'desc',
		},
	],
}
const meters: DataTableDefinition = {
	columns: [
		{
			key: 'hp_id',
		},
		{
			hide: 'hidden-sm-and-down',
			key: 'device_type.description',
		},
		{
			ifNull: '-',

			key: 'unit.hp_id',
			format: FormatValue.getHpIdColumn,
			pickKey: ['unit.hp_id', 'unit.position'],
			keyJoin: ' - ',
		},
		{
			hide: 'hidden-md-and-down',
			key: 'installation_place',
		},
		{
			hide: 'hidden-md-and-down',
			key: 'removed_at',
			align: 'center',
			format: FormatValue.getLocalDateFromCarbon,
		},
		{
			hide: 'hidden-md-and-down',
			key: 'last_accepted_level.value',
			sortable: false,
		},
		{
			key: 'last_accepted_level.read_off_at',
			format: FormatValue.getLocalDateFromCarbon,
			hide: 'hidden-lg-and-down',

			sortable: false,
		},
	],
	child: {
		name: 'buildingMeterDetails',
		endpoint: 'meter',
		params: [
			{
				key: 'id',
				value: 'meterId',
			},
		],
	},
	endpoint: 'meter',
	filters: [filters.metertypes],
	sortBy: [
		{
			key: 'hp_id',
			order: 'asc',
		},
	],
}
const meterLevelRelease: DataTableDefinition = {
	columns: [
		{
			key: 'building',
			pickKey: [
				'meter.building.address.street',
				'meter.building.address.zip',
				'meter.building.address.city',
			],
			sortable: false,
			keyJoin: ', ',
		},
		{
			hide: 'hidden-sm-and-down',
			key: 'meter.unit.position',
			sortable: false,
		},
		{
			key: 'meter.hp_id',
			sortable: false,
		},
		/*{

            hide: 'hidden-md-and-down',
            key: 'meter.device_type.description',
            sortable: false,
        },*/
		{
			align: 'center',
			key: 'value',
		},
		/*{

            align: 'center',
            hide: 'hidden-xs',
            key: 'meter.last_accepted_level.value',
            sortable: false,
        },*/
		{
			hide: 'hidden-md-and-down',
			key: 'read_off_at',
			format: FormatValue.getLocalDateTimeFromCarbon,
		},
		{
			hide: 'hidden-md-and-down',
			key: 'created',
			sort: 'created_at',
			format: FormatValue.getLocalDateTimeFromCarbon,
		},
	],
	filters: [
		filters.buildingsMeter,
		{
			...filters.metertypes,
			field: 'meter.device_type_id',
		},
	],
	sortBy: [
		{
			key: 'id',
			order: 'desc',
		},
	],
	child: {
		name: 'administrationMeterDetails',
		endpoint: 'meters',
		params: [
			{
				key: 'id',
				value: 'id',
			},
			{
				key: 'meter.id',
				value: 'meterId',
			},
		],
	},
	endpoint: 'meterLevel',
}
const handoverprotocols: DataTableDefinition = {
	columns: [
		{
			key: 'unit.position',
		},
		{
			align: 'center',
			hide: 'hidden-md-and-down',

			key: 'date',
			format: FormatValue.getLocalDateTimeFromCarbon,
		},
		{
			align: 'center',
			hide: 'hidden-md-and-down',

			key: 'initial_inspection',
			format: FormatValue.getLocalDateTimeFromCarbon,
		},
		{
			align: 'center',
			hide: 'hidden-md-and-down',

			key: 'final_inspection',
			format: FormatValue.getLocalDateTimeFromCarbon,
		},
		{
			align: 'center',
			hide: 'hidden-md-and-down',

			key: 'grace_period',
			format: FormatValue.getLocalDateTimeFromCarbon,
		},
		{
			align: 'center',
			size: 'xs',

			key: 'completed',
			format: FormatValue.getCheckOrCrossFromBool,
		},
	],
	child: {
		name: 'handOverProtocolEdit',
		endpoint: 'handoverprotocols',
		params: [
			{
				key: 'id',
				value: 'handoverId',
			},
		],
	},
	endpoint: 'handoverprotocols',
	sortBy: [
		{
			key: 'id',
			order: 'asc',
		},
	],
}
const handoverProtocolsAccounts: DataTableDefinition = {
	columns: [
		{
			key: 'name',
		},
		{
			key: 'email',
			format: FormatValue.getMailHref,
		},
		{
			key: 'tos&privacy',
			pickKey: ['person.user.tos', 'person.user.privacy'],
			format: FormatValue.getTosAndPolicy,
		},
	],
	endpoint: 'users',
	sortBy: [
		{
			key: 'id',
			order: 'asc',
		},
	],
}

const mailserver: DataTableDefinition = {
	columns: [
		{
			key: 'active',
			format: FormatValue.getCheckOrCrossFromBool,
		},
		{
			key: 'address',
			pickKey: ['url', 'port'],
			keyJoin: ':',
		},
		{
			key: 'username',
			hide: 'hidden-md-and-down',
		},
		{
			key: 'sender',
			pickKey: ['sender_address', 'sender_name'],
			hide: 'hidden-sm-and-down',
			keyJoin: ' - ',
		},
		{
			key: 'reply_to',
			pickKey: ['reply_to', 'reply_to_name'],
			hide: 'hidden-sm-and-down',
			ifNull: '-',
		},
		{
			key: 'encryption',
			hide: 'hidden-md-and-down',
			align: 'center',
		},
	],
	endpoint: 'mailservers',
	sortBy: [
		{
			key: 'active',
			order: 'desc',
		},
	],
	child: {
		name: 'mailserversettingsedit',
		params: [
			{
				key: 'id',
				value: 'id',
			},
		],
	},
}

const notes: DataTableDefinition = {
	child: {
		name: 'noteView',
		params: [
			{
				key: 'id',
				value: 'noteId',
			},
		],
	},
	columns: [
		{
			key: 'subject',
			size: 'lg',
		},
		{
			key: 'notes_types.name',
			hide: 'hidden-xs',
			size: 'md',
		},
		{
			key: 'date_start',
			hide: 'hidden-md-and-down',
			size: 'sm',
			format: FormatValue.getLocalDateFromCarbon,
		},
		{
			key: 'date_end',
			hide: 'hidden-md-and-down',
			size: 'sm',
			format: FormatValue.getLocalDateFromCarbon,
		},
		{
			key: 'valid_until',
			hide: 'hidden-md-and-down',
			size: 'sm',
			format: FormatValue.getLocalDateFromCarbon,
		},
		{
			key: 'priority',
			hide: 'hidden-xs',
			size: 'xs',
		},
	],
	endpoint: 'notes',
	filters: [filters.buildingsNotes, filters.notestype],
	sorts: [
		{
			text: 'Art',
			value: 'notes_types.id',
		},
		{
			text: 'Betreff',
			value: 'subject',
		},
		{
			text: 'Erstellt am',
			value: 'created_at',
		},
		{
			text: 'Priorität',
			value: 'priority',
		},
	],
	sortBy: [
		{
			key: 'created_at',
			order: 'desc',
		},
	],
}
const notificationsWithoutTosPrivacy: DataTableDefinition = {
	child: {
		name: 'notificationView',
		params: [
			{
				key: 'id',
				value: 'id',
			},
		],
	},
	columns: [
		{
			key: 'type',
			formatWithItem: FormatValue.getNotificationType,
		},
		{
			key: 'data.email',
		},
		{
			key: 'created_at',
			format: FormatValue.getLocalDateTimeFromCarbon,
			hide: 'hidden-xs',
		},
		{
			key: 'read_at',
			format: FormatValue.getLocalDateTimeFromCarbon,
			hide: 'hidden-xs',
		},
	],
	endpoint: 'notifications/team',
	filters: [filters.users],
	sortBy: [
		{
			key: 'created_at',
			order: 'desc',
		},
	],
}
const notifications: DataTableDefinition = {
	columns: [
		{
			key: 'type',
			format: FormatValue.getNotificationType,
		},
		{
			key: 'data.email',
		},
		{
			key: 'created_at',
			format: FormatValue.getLocalDateTimeFromCarbon,
			hide: 'hidden-xs',
		},
		{
			key: 'read_at',
			format: FormatValue.getLocalDateTimeFromCarbon,
			hide: 'hidden-xs',
		},
	],
	endpoint: 'notifications/team',
	filters: [filters.users],
	sortBy: [
		{
			key: 'created_at',
			order: 'desc',
		},
	],
}
const notificationsBase: DataTableDefinition = {
	endpoint: 'notifications',
	columns: [],
	sortBy: [
		{
			key: 'created_at',
			order: 'desc',
		},
	],
}
const notificationsUnread: DataTableDefinition = {
	...notificationsBase,
	endpoint: notificationsBase.endpoint + '/unread',
}
const notificationsRead: DataTableDefinition = {
	...notificationsBase,
	endpoint: notificationsBase.endpoint + '/read',
}

const permissions: DataTableDefinition<Api.RolePermission> = {
	child: [
		{
			name: 'unit',
			endpoint: 'unit',
			params: [
				{
					key: 'unit_id',
					value: 'unitId',
				},
				{
					key: 'unit.building_id',
					value: 'id',
				},
			],
		},
		{
			name: 'building',
			endpoint: 'building',
			params: [
				{
					key: 'building_id',
					value: 'id',
				},
			],
		},
	],
	columns: [
		{
			key: 'role.hp_id',
			pickKey: ['role.hp_id', 'role.name'],
			keyJoin: ' - ',
			size: 'md',
		},
		{
			key: 'building.hp_id',
			format: FormatValue.getHpIdColumn,
			pickKey: ['building.hp_id', 'building.address.street'],
			keyJoin: ' - ',
			ifNull: ['unit.building.hp_id', 'unit.building.address.street'],
			size: 'lg',
		},
		{
			key: 'unit.hp_id',
			format: FormatValue.getHpIdColumn,
			pickKey: ['unit.hp_id', 'unit.position'],
			keyJoin: ' - ',
			size: 'lg',
		},
		{
			key: 'started_at',
			size: 'md',
			format: FormatValue.getLocalDateFromCarbon,
			hide: 'hidden-sm-and-down',
			sortable: false,
		},
		{
			key: 'ended_at',
			size: 'md',
			format: FormatValue.getLocalDateFromCarbon,
			hide: 'hidden-sm-and-down',
			sortable: false,
		},
	],
	sortBy: [
		{
			key: 'role.name',
			order: 'asc',
		},
	],
	relatesTo: [
		{
			collection: 'persons',
			column: 'person_id',
		},
	],
}
const persons: DataTableDefinition = {
	child: {
		name: 'person',
		endpoint: 'persons',
		params: [
			{
				key: 'id',
				value: 'id',
			},
		],
	},
	columns: [
		{
			key: 'hp_id',
		},
		{
			pickKey: ['salutation', 'title', 'firstname', 'lastname'],
			key: 'lastname',
		},
		{
			key: 'address.street',
			hide: 'hidden-xs',
		},
		{
			key: 'address.zip',
			hide: 'hidden-md-and-down',
		},
		{
			key: 'address.city',
			hide: 'hidden-xs',
		},
		{
			key: 'role.name',
			hide: 'hidden-xs',
		},
		{
			key: 'role.started_at',
			hide: 'hidden-md-and-down',
			format: FormatValue.getLocalDateFromCarbon,
			sortable: false,
		},
		{
			key: 'role.ended_at',
			hide: 'hidden-md-and-down',
			format: FormatValue.getLocalDateFromCarbon,
			sortable: false,
		},
		{
			key: 'user_id',
			format: FormatValue.getCheckOrCrossFromBool,
			hide: 'hidden-sm-and-down',
			sortable: false,
		},
	],
	endpoint: 'persons',
	filters: [filters.rolepermissions],
	relations: '["address"]',
	sortBy: [
		{
			key: 'hp_id',
			order: 'asc',
		},
	],
}
const noteRecipients: DataTableDefinition = {
	columns: [
		{
			key: 'user.tos', // 'user.privacy'
			format: FormatValue.getTosAndPolicy,
			pickKey: ['user.tos', 'user.privacy'],
			type: columnTypes.icon,
		},
		{
			key: 'hp_id',
			hide: 'hidden-md-and-down',
		},
		{
			key: 'lastname',
			pickKey: ['salutation', 'title', 'firstname', 'lastname'],
		},
		{
			key: 'user.email',
		},
	],
	multiple: {
		...permissions,
		...{
			child: [
				{
					name: 'unit',
					endpoint: 'unit',
					params: [
						{
							key: 'unit.id',
							value: 'unitId',
						},
						{
							key: 'unit.building_id',
							value: 'id',
						},
					],
				},
				{
					name: 'building',
					endpoint: 'building',
					params: [
						{
							key: 'building.id',
							value: 'id',
						},
					],
				},
			],
			hideColumns: ['Liegenschaft'],
		},
		icon: '$person_function',
		linkItem: 'roles',
		name: 'Personenfunktion(en)',
		linkFirstItem: true,
	},
	endpoint: 'persons',
	sortBy: [
		{
			key: 'hp_id',
			order: 'asc',
		},
	],
}
const units: DataTableDefinition = {
	...multipleUnitDefinition,
	child: {
		name: 'unit',
		endpoint: 'units',
		params: [
			{
				key: 'id',
				value: 'unitId',
			},
			{
				key: 'building_id',
				value: 'id',
			},
		],
	},
	endpoint: 'units',
}
const unitsMeters: DataTableDefinition = {
	...meters,
	sortBy: [
		{
			key: 'id',
			order: 'asc',
		},
	],
	columns: [
		{
			key: 'hp_id',
		},
		{
			key: 'device_type.description',
		},
		{
			key: 'installation_place',
			hide: 'hidden-md-and-down',
		},
		{
			hide: 'hidden-md-and-down',
			key: 'removed_at',
			align: 'center',
			format: FormatValue.getLocalDateFromCarbon,
		},
		{
			key: 'last_accepted_level.value',
			sortable: false,
		},
		{
			key: 'last_accepted_level.read_off_at',
			sortable: false,
			format: FormatValue.getLocalDateFromCarbon,
			hide: 'hidden-md-and-down',
		},
	],
	child: {
		name: 'unitMeterDetails',
		endpoint: 'meter',
		params: [
			{
				key: 'id',
				value: 'meterId',
			},
		],
	},
}
const personsBuildingsGrouped: DataTableDefinition = {
	...persons,
	...{
		columns: [
			{
				key: 'lastname',
				pickKey: ['salutation', 'title', 'firstname', 'lastname'],
			},
			{
				key: 'user_id',
				format: FormatValue.getCheckOrCrossFromBool,
				hide: 'hidden-md-and-down',
			},
		],
		multiple: {
			...permissions,
			...{
				child: [
					{
						name: 'unit',
						endpoint: 'unit',
						params: [
							{
								key: 'unit.id',
								value: 'unitId',
							},
							{
								key: 'unit.building_id',
								value: 'id',
							},
						],
					},
					{
						name: 'building',
						endpoint: 'building',
						params: [
							{
								key: 'building.id',
								value: 'id',
							},
						],
					},
				],
				hideColumns: ['Liegenschaft'],
			},
			icon: 'fa-fw fas fa-user-friends',
			linkItem: 'roles',
			name: 'Personenfunktion(en)',
			linkFirstItem: true,
		},
		expand: true,
	},
}
const personsBuildingsList: DataTableDefinition = {
	...persons,
	...{
		child: {
			name: 'person',
			endpoint: 'persons',
			params: [
				{
					key: 'person.id',
					value: 'id',
				},
			],
		},
		columns: [
			{
				pickKey: [
					'person.salutation',
					'person.title',
					'person.firstname',
					'person.lastname',
				],
				key: 'person.lastname',
			},
			{
				key: 'role.name',
				hide: 'hidden-xs',
			},
			{
				key: 'unit.position',
			},
			{
				key: 'started_at',
				hide: 'hidden-sm-and-down',
				format: FormatValue.getLocalDateFromCarbon,
			},
			{
				key: 'ended_at',
				hide: 'hidden-sm-and-down',
				format: FormatValue.getLocalDateFromCarbon,
			},
			{
				key: 'person.user_id',
				format: FormatValue.getCheckOrCrossFromBool,
				hide: 'hidden-md-and-down',
			},
		],
	},
}
const phonenumbers: DataTableDefinition = {
	columns: [
		{
			key: 'number',
			size: 'lg',
			format: FormatValue.getPhoneHref,
		},
		{
			key: 'description',
			hide: 'hidden-xs',
			size: 'xs',
		},
		{
			key: 'notice',
			hide: 'hidden-sm-and-down',
			size: 'xs',
		},
	],
	relatesTo: [
		{
			collection: 'persons',
			column: 'person_id',
		},
	],
	sortBy: [
		{
			key: 'hp_id',
			order: 'asc',
		},
	],
}

const pendingReports: DataTableDefinition = {
	columns: [],
	sortBy: [
		{
			key: 'last_updated',
			order: 'asc',
		},
	],
	filters: [
		{
			...filters.buildings,
			...{ key: 'building_id', name: 'Liegenschaft' },
		},
		{
			...filters.teams,
			...{ key: 'assigned_team_id', name: 'Sachbearbeiter' },
		},
		filters.reportStatus,
		{
			// LIMITATION: filters only portal-user authors; accountless reports (author_person_id)
			// are un-filterable until the API reports endpoint supports that query param. (AAR-1555/1556)
			...filters.users,
			...{ key: 'author_user_id', name: 'Ersteller' },
		},
	],
}
const reportStatuses: DataTableDefinition = {
	columns: [
		{
			key: 'name',
		},
		{
			key: 'text_color',
			format: FormatValue.getColorBadge,
		},
	],
	child: {
		name: 'settingsReportStatusEdit',
		params: [
			{
				key: 'id',
				value: 'reportStatusId',
			},
		],
	},
	endpoint: 'report_statuses',
	sortBy: [
		{
			key: 'ordering',
			order: 'asc',
		},
	],
}

const profileReports: DataTableDefinition = {
	columns: [
		{
			key: 'id',
			hide: 'hidden-md-and-down',
		},
		{
			key: 'report_status',
			sortable: false,
			format: FormatValue.getReportStatusBadge,
		},
		{
			// AAR-1842: Die Avatar-Spalte ist nicht sortierbar. `assigned_team` zeigt auf
			// die Tabelle `team`, und die fuehrt `user_id`, `first_name`, `last_name` und
			// `salutation` — kein `user`. Ohne diesen Riegel schickt ein Klick auf den
			// Spaltenkopf `of=assigned_team.user` an die API; seit AAR-1839 antwortet die
			// mit einer unsortierten Liste statt mit 500, der Klick bleibt also wirkungslos.
			// Sortieren nach Name bietet bereits die Spalte darunter an.
			key: 'assigned_team.user',
			sortable: false,
			hide: 'hidden-md-and-down',
			align: 'center',
			format: FormatValue.getUserImage,
		},
		{
			pickKey: [
				'assigned_team.salutation',
				'assigned_team.first_name',
				'assigned_team.last_name',
			],

			hide: 'hidden-xs',
			size: 'lg',
			key: 'assigned_team.last_name',
		},
		{
			pickKey: [
				'author_user.persons[0].salutation',
				'author_user.persons[0].title',
				'author_user.persons[0].firstname',
				'author_user.persons[0].lastname',
				// Account-less reporter: author_user is null, name comes from author_person.
				// Exactly one author branch is populated; pickKey skips the empty one.
				'author_person.salutation',
				'author_person.title',
				'author_person.firstname',
				'author_person.lastname',
			],
			key: 'author',
		},
		{
			key: 'type',
			hide: 'hidden-md-and-down',
			pickKey: ['report_sub_type.report_type.name', 'report_sub_type.name'],
			keyJoin: ' / ',
			sortable: false,
		},
		{
			pickKey: [
				'building.address.street',
				'building.address.zip',
				'building.address.city',
			],
			key: 'building',
			sortable: false,
			keyJoin: ', ',
		},
		{
			key: 'unit.position',
		},
		{
			key: 'created',
			hide: 'hidden-md-and-down',
			format: FormatValue.getLocalDateTimeFromCarbon,
			sort: 'created_at',
		},
	],
	child: {
		name: 'reportView',
		params: [
			{
				key: 'id',
				value: 'id',
			},
		],
	},
	endpoint: 'reports',
	relations: '["building","unit", "assigned_team"]',
	filters: [
		{
			...filters.buildings,
			...{ key: 'building_id', name: 'Liegenschaft' },
		},
		filters.reportStatus,
	],
	sortBy: [
		{
			key: 'created_at',
			order: 'desc',
		},
	],
}

const roles: DataTableDefinition = {
	columns: [
		{
			key: 'hp_id',
			hide: 'hidden-md-and-down',
			size: 'xs',
		},
		{
			key: 'name',
		},
		{
			key: 'em',
			hide: 'hidden-sm-and-down',
			align: 'center',
			size: 'xs',
		},
		{
			key: 'persons_count',
		},
	],
	child: {
		name: 'persons',
		endpoint: 'persons',
		filter: filters.role,
		setFilter: true,
	},
	endpoint: 'roles',
	relations: '["persons"]',
	params: {
		count: 'persons',
	},
	sortBy: [
		{
			key: 'name',
			order: 'asc',
		},
	],
}
const rolesBuilding: DataTableDefinition = {
	...roles,
	child: {
		...roles.child,
		name: 'buildingPersonsList',
	},
}
const rolesPersons: DataTableDefinition = {
	...roles,
	multiple: {
		...persons,
		...{
			child: undefined,
		},
		icon: 'fa-fw far fa-address-book',
		linkItem: 'persons',
		name: 'Person(en)',
	},
}
const rolePermissions: DataTableDefinition = {
	...personsBuildingsList,
	...{
		columns: [
			{
				pickKey: [
					'person.salutation',
					'person.title',
					'person.firstname',
					'person.lastname',
				],
				key: 'person.lastname',
			},
			{
				key: 'role.name',
				hide: 'hidden-xs',
			},
			{
				pickKey: [
					'building.address.street',
					'building.address.zip',
					'building.address.city',
				],
				key: 'building',
				sortable: false,
				keyJoin: ', ',
			},
			{
				key: 'unit.position',
			},
			{
				key: 'started_at',
				hide: 'hidden-sm-and-down',
				format: FormatValue.getLocalDateFromCarbon,
			},
			{
				key: 'ended_at',
				hide: 'hidden-sm-and-down',
				format: FormatValue.getLocalDateFromCarbon,
			},
			{
				key: 'person.user_id',
				format: FormatValue.getCheckOrCrossFromBool,
				hide: 'hidden-md-and-down',
			},
		],
	},
}
/*
 * Die Spalten sind nach Breite gestaffelt (`hide`): auf einem Handy passten
 * sieben Spalten nicht nebeneinander, die Tabelle scrollte waagerecht und die
 * Zeilenmitte lag ausserhalb des Schirms — antippbar und damit erreichbar war
 * das Cockpit mobil dadurch gar nicht. Uebrig bleiben bei xs die drei Spalten,
 * die eine Belegpruefung auseinanderhalten: Objekt, Bezeichnung, Prüfstand.
 */
const reviewDocuments: DataTableDefinition = {
	child: {
		name: 'reviewDocument',
		endpoint: 'review_documents',
		params: [
			{
				key: 'id',
				value: 'reviewDocumentId',
			},
		],
	},
	columns: [
		{
			key: 'hp_id',
			hide: 'hidden-md-and-down',
			size: 'xs',
		},
		{
			key: 'building_id',
			pickKey: 'building.hp_id',
			size: 'xs',
		},
		{
			key: 'building.address.street',
			i18nKey: 'building_name',
			pickKey: ['building.address.street', 'building.address.city'],
			hide: 'hidden-md-and-down',
			size: 'md',
		},
		{
			key: 'accounting_year.year',
			align: 'center',
			hide: 'hidden-xs',
			size: 'xs',
			sortable: false,
		},
		/*{
            key: ['accounting_year.started_at', 'accounting_year.ended_at'],
            size: 'xs',
            keyJoin: ' - ',
            format: FormatValue.getLocalDateFromCarbon,
        },*/
		{
			key: 'name',
			align: 'center',
			size: 'md',
		},
		{
			pickKey: ['team.salutation', 'team.first_name', 'team.last_name'],
			key: 'team.last_name',
			align: 'center',
			hide: 'hidden-sm-and-down',
			size: 'md',
		},
		{
			key: 'status',
			align: 'center',
			format: FormatValue.getReviewStateIcon,
			sortable: false,
		},
		{
			key: 'active',
			align: 'center',
			format: FormatValue.getCheckOrCrossFromBool,
			hide: 'hidden-sm-and-down',
			sortable: false,
		},
		{
			key: 'completed',
			format: FormatValue.getLocalDateFromCarbon,
			align: 'center',
			hide: 'hidden-sm-and-down',
			ifNull: '-',
		},
	],
	endpoint: 'review_documents',
	relations: '["building", "accounting_year", "team"]',
	sortBy: [
		{
			key: 'hp_id',
			order: 'asc',
		},
	],
}
const reviewDocumentReviewers: DataTableDefinition = {
	columns: [],
	sortBy: [
		{
			key: 'created_at',
			order: 'desc',
		},
	],
}
const tasks: DataTableDefinition = {
	columns: [
		{
			key: 'task_type.name',
			sortable: false,
		},
		{
			key: 'text',
			size: 'lg',
			sortable: false,
		},
		{
			key: 'due_date',
			format: FormatValue.getLocalDateFromCarbon,
		},
		{
			key: 'team_id_assigned',
			pickKey: [
				'team_assigned.salutation',
				'team_assigned.name',
				'team_assigned.last_name',
			],
			sortable: false,
		},
	],
	endpoint: 'tasks',
	sortBy: [
		{
			key: 'due_date',
			order: 'asc',
		},
	],
	filters: [
		filters.tasktype,
		{
			...filters.teams,
			...{ key: 'team_id_assigned', name: 'component.teams.assigned_to' },
		},
		{
			...filters.date,
			...{
				key: 'due_date',
				name: 'Fällig am',
				type: 'autocomplete',
				value: {
					field: 'raw',
					display: 'formatted',
				},
			},
		},
	],
}

const rolesWithCount: DataTableDefinition = {
	child: {
		name: 'rolepermission',
		endpoint: 'roles',

		params: [
			{
				key: 'id',
				value: 'id',
			},
		],
	},
	endpoint: 'roles',
	relations: '["persons"]',
	params: {
		count: 'persons',
	},
	columns: roles.columns,
	sortBy: [
		{
			key: 'hp_id',
			order: 'asc',
		},
	],
}
const teams: DataTableDefinition = {
	columns: [
		{
			key: 'user',
			format: FormatValue.getUserImage,

			align: 'center',
			size: 'sm',
			sortable: false,
		},
		{
			key: 'last_name',
			pickKey: ['salutation', 'first_name', 'last_name'],
			size: 'lg',
		},
		// {
		// 	key: 'user.email',
		// 	title: 'E-Mail',
		// 	size: 'lg',
		// },
		{
			align: 'center',
			key: 'active',
			format: FormatValue.getCheckOrCrossFromBool,
			size: 'xs',
			sortable: false,
		},
		{
			align: 'center',
			key: 'can_delete',
			format: FormatValue.getCheckOrCrossFromBool,
			hide: 'hidden-md-and-down',

			size: 'xs',
			sortable: false,
		},
		{
			align: 'center',
			key: 'can_update',
			format: FormatValue.getCheckOrCrossFromBool,
			hide: 'hidden-md-and-down',

			size: 'xs',
			sortable: false,
		},
		{
			align: 'center',
			key: 'can_post',
			format: FormatValue.getCheckOrCrossFromBool,
			hide: 'hidden-md-and-down',

			size: 'xs',
			sortable: false,
		},
		{
			align: 'center',
			key: 'can_settings',
			format: FormatValue.getCheckOrCrossFromBool,
			hide: 'hidden-md-and-down',

			size: 'xs',
			sortable: false,
		},
		{
			align: 'center',
			key: 'can_view_all_reports',
			format: FormatValue.getCheckOrCrossFromBool,
			hide: 'hidden-md-and-down',

			size: 'xs',
			sortable: false,
		},
	],
	child: {
		name: 'teamEdit',
		endpoint: 'teams',
		params: [
			{
				key: 'id',
				value: 'id',
			},
		],
	},
	// with_inactive=1: the management list must surface deactivated members
	// (marked via the `active` column above); List.vue's "show inactive" switch
	// narrows this back down to plain 'teams' (active-only, the API default).
	endpoint: 'teams?with_inactive=1',
	sortBy: [
		{
			key: 'id',
			order: 'asc',
		},
	],
}
const personUpdateRequests: DataTableDefinition = {
	columns: [
		{
			key: 'person.lastame',
			pickKey: ['person.firstname', 'person.lastname'],
			size: 'xs',
		},
		{
			key: 'updated',
			format: FormatValue.getLocalDateTimeFromCarbon,
			size: 'xs',
			sort: 'updated_at',
		},
		{
			key: 'rejected_at',
			format: FormatValue.getLocalDateTimeFromCarbon,
			size: 'xs',
		},
		{
			key: 'approved_at',
			format: FormatValue.getLocalDateTimeFromCarbon,
			size: 'xs',
		},
		{
			key: 'synced_at',
			format: FormatValue.getLocalDateTimeFromCarbon,
			size: 'xs',
		},
	],
	endpoint: 'person_updaterequests',
	child: {
		name: 'approveUpdaterequest',
		params: [{ key: 'id', value: 'id' }],
	},
	sortBy: [
		{
			key: 'id',
			order: 'asc',
		},
	],
}
const syncs: DataTableDefinition = {
	child: {
		name: 'sync',
		endpoint: 'syncs',
		params: [
			{
				key: 'id',
				value: 'id',
			},
		],
	},
	columns: [
		{
			key: 'started_at',
			format: FormatValue.getLocalDateTimeFromCarbon,
		},
		{
			key: 'ended_at',
			format: FormatValue.getLocalDateTimeFromCarbon,
		},
		{
			key: 'total_count',
			align: 'center',
			sortable: false,
		},
	],
	disabled: [
		{
			key: 'total_count',
			operator: '===',
			value: 0,
		},
	],
	endpoint: 'syncs',
	sortBy: [
		{
			key: 'started_at',
			order: 'desc',
		},
	],
}
const signeesPending: DataTableDefinition = {
	columns: [
		{
			key: 'person.hp_id',
			hide: 'hidden-md-and-down',
			ifNull: '-',
			align: 'center',
			size: 'xs',
		},
		{
			key: 'lastname',
			pickKey: [
				'person.salutation',
				'person.title',
				'person.firstname',
				'person.lastname',
			],
		},
		{
			align: 'center',

			key: 'role',
			format: FormatValue.getSigneeRole,
			hide: 'hidden-xs',
		},
		{
			align: 'center',
			key: 'person.user.email',
			hide: 'hidden-sm-and-down',
			sortable: false,
		},
		{
			align: 'center',
			format: FormatValue.getSigningType,
			key: 'signed_via',
			sortable: false,
		},
	],
}

const handoverTemplates: DataTableDefinition = {
	columns: [
		{
			key: 'name',

			size: 'lg',
		},
		{
			key: 'rooms_count',

			align: 'center',
			size: 'xs',
		},
	],
	params: {
		count: 'rooms',
	},
	child: {
		name: 'handOverTemplateEdit',
		endpoint: 'handovertemplates',
		params: [
			{
				key: 'id',
				value: 'id',
			},
		],
	},
	endpoint: 'handovertemplates',
	sortBy: [
		{
			key: 'id',
			order: 'asc',
		},
	],
}

const users: DataTableDefinition<Api.User> = {
	child: persons.child,
	columns: [
		{
			key: 'hp_id',
			hasMultiple: true,
			align: 'center',
			hide: 'hidden-md-and-down',
			sortable: false,
			size: 'sm',
		},
		{
			key: 'lastname',
			pickKey: ['salutation', 'firstname', 'lastname'],
			hasMultiple: true,
			hide: 'hidden-xs',
			sortable: false,
			size: 'xl',
		},
		{
			key: 'email',
			format: FormatValue.getMailHref,
			size: 'lg',
		},
		{
			key: 'tos',
			hide: 'hidden-lg-and-down',
			align: 'center',
			format: FormatValue.getCheckOrCrossFromBool,
			size: 'xs',
		},
		{
			key: 'privacy',
			hide: 'hidden-lg-and-down',
			align: 'center',
			format: FormatValue.getCheckOrCrossFromBool,
			size: 'xs',
		},
		{
			key: 'logins_count',
			align: 'center',
			hide: 'hidden-md-and-down',
			size: 'xs',
		},
		{
			key: 'welcome',
			pickKey: 'welcome',
			align: 'center',
			format: FormatValue.getLocalDateTimeFromCarbon,
			hide: 'hidden-md-and-down',
			size: 'md',
		},
		{
			key: 'created_at',
			align: 'center',
			format: FormatValue.getLocalDateTimeFromCarbon,
			hide: 'hidden-md-and-down',
			pickKey: 'created',
			size: 'md',
		},
		{
			pickKey: 'latest_login.created',
			format: FormatValue.getLocalDateTimeFromCarbon,
			key: 'latest_login.created_at',
			align: 'center',
			hide: 'hidden-md-and-down',
			size: 'md',
		},
	],
	multiple: {
		...persons,
		...{
			hideColumns: [
				'entity.person.fields.started_at',
				'entity.person.fields.ended_at',
				'entity.person.fields.role.name',
				'entity.person.fields.address.street',
				'entity.person.fields.address.zip',
				'entity.person.fields.address.city',
			],
		},
		icon: 'fa-fw far fa-address-book',
		linkItem: 'persons',
		name: 'Person(en)',
	},
	endpoint: 'users',
	relations: '["persons", "latest_login"]',
	sortBy: [
		{
			key: 'id',
			order: 'asc',
		},
	],
	expand: true,
}
const personsAccounts: DataTableDefinition = {
	child: {
		name: 'person',
		endpoint: 'users',
		params: [
			{
				key: 'id',
				value: 'id',
			},
		],
	},
	// personsAccounts wird ausschließlich vom Zugangsdaten-Dialog
	// (SendGreeting.vue, collection: 'persons') genutzt. Die Spaltenbreiten sind
	// daher direkt hier dialogtauglich gesetzt; der Dialog braucht für diesen
	// Pfad keinen zusätzlichen Override.
	columns: [
		{
			key: 'hp_id',
			align: 'center',
			hide: 'hidden-md-and-down',
			size: 'xs',
		},
		{
			key: 'lastname',
			pickKey: ['salutation', 'firstname', 'lastname'],
			hide: 'hidden-xs',
			size: 'md',
			breakWords: 'break-word',
		},
		{
			key: 'user.email',
			format: FormatValue.getMailHref,
			width: 240,
			breakWords: 'break-word',
		},
		{
			key: 'tos',
			pickKey: 'user.tos',
			hide: 'hidden-lg-and-down',
			align: 'center',
			format: FormatValue.getCheckOrCrossFromBool,
			size: 'xs',
		},
		{
			key: 'privacy',
			pickKey: 'user.privacy',
			hide: 'hidden-lg-and-down',
			align: 'center',
			format: FormatValue.getCheckOrCrossFromBool,
			size: 'xs',
		},
		{
			key: 'user.logins_count',
			align: 'center',
			hide: 'hidden-md-and-down',
			size: 'xs',
		},
		{
			key: 'created_at',
			pickKey: 'user.created_at',
			align: 'center',
			format: FormatValue.getLocalDateFromCarbon,
			hide: 'hidden-md-and-down',
			size: 'sm',
		},
		{
			key: 'user.last_login.created_at',
			align: 'center',
			format: FormatValue.getLocalDateFromCarbon,
			hide: 'hidden-md-and-down',
			size: 'sm',
		},
	],
	endpoint: 'personsaccounts',
	relations: '["persons"]',
	sortBy: [
		{
			key: 'id',
			order: 'asc',
		},
	],
}

const records: DataTableDefinition = {
	endpoint: 'records',
	sortBy: [
		{
			key: 'hp_id',
			order: 'asc',
		},
	],
	columns: [
		{
			key: 'hp_id',
			format: FormatValue.parseRecordHpid,
			hide: 'hidden-sm-and-down',
			size: 'xs',
		},
		{
			key: 'search',
			size: 'xs',
		},
		{
			key: 'status.description',
			size: 'xs',
		},
	],
}

const tickets: DataTableDefinition<Api.Ticket> = {
	endpoint: 'tickets',
	// relations: '["ticket_type"]',
	sortBy: [{ key: 'hp_id', order: 'asc' }],
	// filters: [],
	columns: [
		{
			key: 'hp_id',
			hide: 'hidden-sm-and-down',
		},
		{
			key: '',
			pickKey: ['first_building.address.street', 'first_building.address.city'],
			size: 'xs',
			sortable: false,
		},
		{
			key: 'first_unit.position',
			ifNull: '-',
			sortable: false,
		},
		{
			key: 'first_record.search',
			hide: 'hidden-sm-and-down',
			sortable: false,
		},
		{
			key: 'ticket_type.name',
			hide: 'hidden-sm-and-down',
			sortable: false,
		},
		{
			key: 'done',
			format: FormatValue.getCheckOrCrossFromBool,
			hide: 'hidden-sm-and-down',
			sortable: false,
		},
		{
			key: 'active',
			format: FormatValue.getCheckOrCrossFromBool,
			hide: 'hidden-sm-and-down',
			sortable: false,
		},
	],
	child: {
		name: 'ticket',
		endpoint: 'tickets',
		params: [
			{
				key: 'id',
				value: 'id',
			},
		],
	},
	multiple: {
		...records,
		icon: 'fa-regular fa-folders',
		linkItem: 'records',
		name: 'Akte(n)',
		linkFirstItem: true,
	},
}

const revisions: DataTableDefinition = {
	endpoint: 'revisions',
	columns: [],
	sortBy: [
		{
			key: 'created_at',
			order: 'desc',
		},
	],
}

const readConfirmations: DataTableDefinition = {
	endpoint: 'readables',
	columns: [
		{
			key: 'user_fullname',
			i18nKey: 'user_fullname',
		},
		{
			key: 'user_email',
			i18nKey: 'user_email',
		},
		{
			key: 'read_on',
			i18nKey: 'read_on',
		},
	],
	sortBy: [{ key: 'read_on', order: 'desc' }],
}

const $collections = {
	accounts,
	assfinetContracts,
	buildings,
	buildingsTeam,
	cis,
	cisManagement,
	craftsmanTasks,
	craftsmanTasksBuilding,
	craftsmanTasksReports,
	comments,
	contacts,
	documents,
	documentsBuildings,
	documentcontainer,
	documenttypes,
	documenttypesBuilding,
	documenttypesSettings,
	emails,
	faqCategories,
	handoverTemplates,
	handoverprotocols,
	handoverProtocolsAccounts,
	invoices,
	journalsReviewDocument,
	journals,
	messages,
	meters,
	meterLevelRelease,
	notes,
	notestype,
	notestypeSettings,
	notestypeFavorites,
	noteRecipients,
	notifications,
	notificationsBase,
	notificationsUnread,
	notificationsRead,
	notificationsWithoutTosPrivacy,
	reportTypesSettings,
	permissions,
	persons,
	personUpdateRequests,
	personsBuildingsGrouped,
	personsBuildingsList,
	personsAccounts,
	phonenumbers,
	records,
	readConfirmations,
	profileReports,
	profileReportCraftsmanTasks,
	pendingReports,
	reviewDocuments,
	reviewDocumentReviewers,
	roles,
	rolesBuilding,
	rolePermissions,
	rolesPersons,
	reportStatuses,
	mailserver,
	rolesWithCount,
	revisions,
	signeesPending,
	syncs,
	tasks,
	teams,
	tickets,
	units,
	unitsMeters,
	users,
}

export type Collections = keyof typeof $collections

export default $collections
